import {useState, useEffect, useRef} from 'react'
import {Modal, Button, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useAuth} from '../app/modules/auth'
import {useIncomingCall} from '../hooks/useIncomingCall'
import {useAWSConnect} from '../app/context/ConnectStreamsContext'
import {pushNotificationService} from '../services/pushNotificationApi'

/**
 * Center-screen prompt shown to an OPI interpreter when the backend reports (over InterpreterHub)
 * that their notification token is missing/stale (`hasDeviceToken === false`). Left unfixed the
 * interpreter silently misses on-demand calls. The CTA re-inits FCM/SW when possible, otherwise
 * heartbeats the cached token, and restarts the poll loop so LastUsedAt stays fresh.
 *
 * Gating:
 *  - interpreters only, and only those with OPI on-demand permission
 *  - never while the interpreter is on / receiving a call (would interrupt live work)
 *  - auto-dismisses when the backend pushes `hasDeviceToken === true` after the fix
 */
const TokenUpdateModal = () => {
  const {currentUser} = useAuth()
  const {hasDeviceToken, activeCalls, setTokenPromptActive} = useIncomingCall()
  const {isConnected, isIncomingCall, activeCallData} = useAWSConnect()

  // Require the "broken" state to hold for a short grace period before showing, so the brief
  // window on login/reconnect — between the hub sending a stale `false` and register-device
  // landing (which flips it back to true within seconds) — never flashes the modal.
  const GRACE_MS = 20000

  const [isUpdating, setIsUpdating] = useState(false)
  const [graceElapsed, setGraceElapsed] = useState(false)
  const confirmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isInterpreter = currentUser?.result?.userType === 'INTERPRETER'
  const hasOpi = Boolean(currentUser?.result?.opI_OndemandTelephonic)
  const onACall = isConnected || isIncomingCall || !!activeCallData || activeCalls.length > 0

  const conditionsMet = isInterpreter && hasOpi && hasDeviceToken === false && !onACall
  const shouldShow = conditionsMet && graceElapsed

  const clearConfirmTimeout = () => {
    if (confirmTimeoutRef.current) {
      clearTimeout(confirmTimeoutRef.current)
      confirmTimeoutRef.current = null
    }
  }

  // Arm the modal only after the broken state persists for GRACE_MS; reset the moment it clears.
  useEffect(() => {
    if (!conditionsMet) {
      setGraceElapsed(false)
      return
    }
    const timer = setTimeout(() => setGraceElapsed(true), GRACE_MS)
    return () => clearTimeout(timer)
  }, [conditionsMet])

  // Stop the spinner once the backend confirms the token is healthy again (modal will unmount).
  useEffect(() => {
    if (hasDeviceToken === true) {
      clearConfirmTimeout()
      setIsUpdating(false)
    }
  }, [hasDeviceToken])

  // Publish whether the modal is on screen so the Connect widget can react (minimize / go offline /
  // disable controls). Reset to false on unmount so a non-interpreter view never stays "active".
  useEffect(() => {
    setTokenPromptActive(shouldShow)
  }, [shouldShow, setTokenPromptActive])

  useEffect(() => {
    return () => {
      clearConfirmTimeout()
      setTokenPromptActive(false)
    }
  }, [setTokenPromptActive])

  const handleForceUpdate = async () => {
    setIsUpdating(true)
    clearConfirmTimeout()
    try {
      const ok = await pushNotificationService.forceUpdateToken()
      if (!ok) {
        setIsUpdating(false)
        toast.error(
          'Could not update your notification token. Please make sure notifications are allowed for this site in your browser, then try again.'
        )
        return
      }
      // Leave the spinner until token:status flips hasDeviceToken to true (effect above).
      // If presence never confirms, re-enable the button so the interpreter can retry.
      confirmTimeoutRef.current = setTimeout(() => {
        confirmTimeoutRef.current = null
        setIsUpdating((still) => {
          if (still) {
            toast.warning(
              'Token was sent, but confirmation is taking longer than expected. You can try again, or refresh the page.'
            )
          }
          return false
        })
      }, 15000)
    } catch (error) {
      setIsUpdating(false)
      toast.error('Something went wrong while updating your notification token. Please try again.')
    }
  }

  if (!shouldShow) {
    return null
  }

  return (
    <Modal show centered backdrop='static' keyboard={false}>
      <Modal.Header>
        <Modal.Title>Notification Token Issue</Modal.Title>
      </Modal.Header>
      <Modal.Body className='text-center'>
        <div className='mb-4'>
          <i className='bi bi-bell-slash text-warning' style={{fontSize: '3rem'}} />
        </div>
        <p className='fs-5 mb-2'>Your notification token isn't updating.</p>
        <p className='text-muted mb-0'>
          Until it's fixed you may not receive incoming calls. Press the button below to force update
          your token.
        </p>
      </Modal.Body>
      <Modal.Footer className='justify-content-center'>
        <Button variant='primary' onClick={handleForceUpdate} disabled={isUpdating}>
          {isUpdating ? (
            <>
              <Spinner as='span' animation='border' size='sm' className='me-2' />
              Updating…
            </>
          ) : (
            'Force Update Token'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default TokenUpdateModal
